import {
  FloatingPortal,
  Placement,
  autoUpdate,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import { memo, useState } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { filterClasses } from "../../Helpers/FilterClasses";

type Classnames = {
  [key: string]: string;
};

interface TooltipProps extends ComponentPropsWithoutRef<"div"> {
  children: ReactNode;
  classNames?: Classnames;
  content: string;
  delay?: { open?: number; close?: number };
  placement?: Placement;
  tooltipOffset?: number;
  transitionDuration?: [number, number];
  zIndex?: number;
}

const BaseTooltip = ({
  classNames = {},
  children,
  content,
  delay = { open: 0, close: 0 },
  transitionDuration = [200, 300],
  placement = "top",
  tooltipOffset = 1,
  zIndex = 100,
  ...rest
}: TooltipProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { context, x, y, refs, strategy } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    placement: placement,
    whileElementsMounted: autoUpdate,
    middleware: [shift(), offset(tooltipOffset)],
  });
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: {
      open: transitionDuration[0],
      close: transitionDuration[1],
    },
    open: {
      opacity: 1,
    },
    close: {
      opacity: 0,
    },
  });
  const hover = useHover(context, {
    move: false,
    delay: {
      open: delay.open,
      close: delay.close,
    },
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  return (
    <>
      <div className={classNames?.container} {...getReferenceProps({ ref: refs.setReference })} {...rest}>
        {children}
      </div>
      <FloatingPortal>
        {isMounted && (
          <div
            {...getFloatingProps({
              ref: refs.setFloating,
            })}
            className={filterClasses("bg-black/90 max-w-80 text-white px-3 py-2 rounded-md text-pretty", classNames?.tooltip)}
            style={{
              position: strategy,
              left: x ?? 0,
              top: y ?? 0,
              zIndex: zIndex,
              ...styles,
            }}
          >
            {content}
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export const Tooltip = memo(BaseTooltip);
